import { useState, useEffect } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { API, CORES_GRAFICO, CULTURAS_TODAS, fromInputDate, statusInfo, perfilLabel } from "../../constants";

export default function AbaRelatorios({ fazenda, usuario }) {
  const [relatorio, setRelatorio] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [secao, setSecao] = useState("geral");
  const [culturaSelecionada, setCulturaSelecionada] = useState("");
  const [dataInicioCultura, setDataInicioCultura] = useState("");
  const [dataFimCultura, setDataFimCultura] = useState("");
  const [filtroStatusCultura, setFiltroStatusCultura] = useState("todos"); // ← novo filtro
  const [carregandoCultura, setCarregandoCultura] = useState(false);
  const [gerandoIA, setGerandoIA] = useState(false);
  const [relatorioCultura, setRelatorioCultura] = useState(null);
  const [relatorioIA, setRelatorioIA] = useState("");
  const [enviandoWhatsapp, setEnviandoWhatsapp] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [destinatario, setDestinatario] = useState("");

  const buscarRelatorio = async () => {
    setCarregando(true);
    try {
      const params = { fazenda_id: fazenda.id };
      if (dataInicio) params.data_inicio = fromInputDate(dataInicio);
      if (dataFim) params.data_fim = fromInputDate(dataFim);
      const res = await axios.get(`${API}/relatorios`, { params });
      setRelatorio(res.data);
    } catch { alert("Erro ao carregar relatório."); }
    setCarregando(false);
  };

  const buscarUsuariosComWhatsapp = async () => {
    try { const res = await axios.get(`${API}/usuarios/${fazenda.id}`); setUsuarios(res.data.filter(u => u.whatsapp && u.ativo)); }
    catch { setUsuarios([]); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { buscarRelatorio(); buscarUsuariosComWhatsapp(); }, [fazenda.id]);

  const buscarHistoricoCultura = async () => {
    if (!culturaSelecionada) return alert("Selecione uma cultura!");
    setCarregandoCultura(true); setRelatorioIA(""); setRelatorioCultura(null);
    try {
      const params = { fazenda_id: fazenda.id, cultura: culturaSelecionada };
      if (dataInicioCultura) params.data_inicio = fromInputDate(dataInicioCultura);
      if (dataFimCultura) params.data_fim = fromInputDate(dataFimCultura);
      const res = await axios.get(`${API}/relatorios/cultura`, { params });
      setRelatorioCultura(res.data);
    } catch { alert("Erro ao buscar histórico."); }
    setCarregandoCultura(false);
  };

  const gerarRelatorioIA = async () => {
    if (!relatorioCultura) return;
    setGerandoIA(true); setRelatorioIA("");
    try {
      // Filtrar tarefas pelo status selecionado antes de enviar para IA
      const tarefasFiltradas = filtroStatusCultura === "todos"
        ? relatorioCultura.tarefas
        : relatorioCultura.tarefas.filter(t => t.status === filtroStatusCultura);
      const res = await axios.post(`${API}/relatorios/cultura/ia`, {
        fazenda: relatorioCultura.fazenda, cultura: relatorioCultura.cultura,
        tarefas: tarefasFiltradas, periodo: relatorioCultura.periodo
      });
      setRelatorioIA(res.data.relatorio);
    } catch { alert("Erro ao gerar relatório com IA."); }
    setGerandoIA(false);
  };

  const enviarWhatsapp = async () => {
    if (!destinatario) return alert("Selecione um destinatário!");
    if (!relatorioIA) return alert("Gere o relatório com IA primeiro!");
    setEnviandoWhatsapp(true);
    try {
      await axios.post(`${API}/relatorios/cultura/whatsapp`, { numero: destinatario, fazenda: relatorioCultura.fazenda, cultura: relatorioCultura.cultura, relatorio: relatorioIA });
      alert("✅ Relatório enviado por WhatsApp!");
    } catch { alert("Erro ao enviar WhatsApp."); }
    setEnviandoWhatsapp(false);
  };

  const statusLabels = { nao_planejada: "🆕 Não planejada", planejada: "📋 Planejada", pendente: "⏳ Pendente", atrasada: "🔴 Atrasada", em_execucao: "▶️ Em execução", pausada: "⏸️ Pausada", concluida: "✅ Concluída" };
  const porStatusData = relatorio ? Object.entries(relatorio.por_status).map(([k, v]) => ({ name: statusLabels[k] || k, value: v })) : [];
  const porCulturaData = relatorio ? Object.entries(relatorio.por_cultura).map(([k, v]) => ({ name: k, value: v })) : [];
  const inputClass = "px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition";
  const linhasRelatorio = relatorioIA.split("\n");

  // Tarefas filtradas para exibição na lista
  const tarefasExibidas = relatorioCultura
    ? (filtroStatusCultura === "todos" ? relatorioCultura.tarefas : relatorioCultura.tarefas.filter(t => t.status === filtroStatusCultura))
    : [];

  return (
    <div className="space-y-4">
      <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-xl gap-1">
        <button onClick={() => setSecao("geral")} className={`flex-1 py-2 text-xs font-semibold rounded-lg transition min-h-11 ${secao === "geral" ? "bg-white dark:bg-gray-700 text-green-700 dark:text-green-400 shadow-sm" : "text-gray-500 dark:text-gray-400"}`}>📊 Relatório Geral</button>
        <button onClick={() => setSecao("cultura")} className={`flex-1 py-2 text-xs font-semibold rounded-lg transition min-h-11 ${secao === "cultura" ? "bg-white dark:bg-gray-700 text-green-700 dark:text-green-400 shadow-sm" : "text-gray-500 dark:text-gray-400"}`}>🌾 Histórico por Cultura</button>
      </div>

      {secao === "geral" && (
        <>
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 border border-gray-100 dark:border-gray-800">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">📅 Filtrar por período</p>
            <div className="flex gap-2 flex-wrap">
              <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} className={inputClass + " flex-1 min-w-36"} />
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} className={inputClass + " flex-1 min-w-36"} />
              <button onClick={buscarRelatorio} disabled={carregando} className="bg-green-600 text-white text-sm font-semibold px-5 py-3 rounded-xl hover:bg-green-700 transition min-h-11">{carregando ? "..." : "Filtrar"}</button>
              <button onClick={() => { setDataInicio(""); setDataFim(""); setTimeout(buscarRelatorio, 100); }} className="border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 text-sm px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition min-h-11">Limpar</button>
            </div>
          </div>
          {carregando && <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse" />)}</div>}
          {relatorio && !carregando && (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {[
                  { label: "Total de tarefas", valor: relatorio.total_tarefas, cor: "text-gray-800 dark:text-white", bg: "bg-white dark:bg-gray-900" },
                  { label: "Taxa de conclusão", valor: `${relatorio.taxa_conclusao}%`, cor: "text-green-600", bg: "bg-green-50 dark:bg-green-900/20" },
                  { label: "Atrasadas", valor: relatorio.atrasadas, cor: "text-red-600", bg: "bg-red-50 dark:bg-red-900/20" },
                  { label: "Em execução", valor: relatorio.em_execucao, cor: "text-blue-600", bg: "bg-blue-50 dark:bg-blue-900/20" },
                ].map((c, i) => (
                  <div key={i} className={`${c.bg} rounded-2xl p-4 border border-gray-100 dark:border-gray-800`}>
                    <p className={`text-2xl font-bold ${c.cor}`}>{c.valor}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{c.label}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 border border-gray-100 dark:border-gray-800">
                  <p className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-3">Tarefas por status</p>
                  <ResponsiveContainer width="100%" height={180}><PieChart><Pie data={porStatusData} cx="50%" cy="50%" outerRadius={65} dataKey="value">{porStatusData.map((_, i) => <Cell key={i} fill={CORES_GRAFICO[i % CORES_GRAFICO.length]} />)}</Pie><Tooltip /></PieChart></ResponsiveContainer>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 border border-gray-100 dark:border-gray-800">
                  <p className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-3">Tarefas por cultura</p>
                  <ResponsiveContainer width="100%" height={180}><BarChart data={porCulturaData}><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="value" fill="#16a34a" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
                </div>
              </div>
              {Object.keys(relatorio.por_prioridade).length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 border border-gray-100 dark:border-gray-800">
                  <p className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-3">Tarefas por prioridade</p>
                  <div className="flex gap-3">
                    {Object.entries(relatorio.por_prioridade).map(([p, v]) => (
                      <div key={p} className={`flex-1 text-center p-3 rounded-xl ${p === "alta" ? "bg-red-50 dark:bg-red-900/20" : p === "media" ? "bg-amber-50 dark:bg-amber-900/20" : "bg-green-50 dark:bg-green-900/20"}`}>
                        <p className={`text-2xl font-bold ${p === "alta" ? "text-red-600" : p === "media" ? "text-amber-600" : "text-green-600"}`}>{v}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">● {p}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {Object.keys(relatorio.por_operador).length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 border border-gray-100 dark:border-gray-800">
                  <p className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-3">👷 Performance por operador</p>
                  <div className="space-y-2">
                    {Object.entries(relatorio.por_operador).map(([nome, dados]) => {
                      const taxa = dados.total > 0 ? Math.round((dados.concluidas / dados.total) * 100) : 0;
                      return (
                        <div key={nome} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                          <div><p className="text-sm font-semibold text-gray-800 dark:text-white">👷 {nome}</p><p className="text-xs text-gray-500 dark:text-gray-400">{dados.total} tarefas · {dados.concluidas} concluídas · {dados.atrasadas} atrasadas</p></div>
                          <span className={`px-3 py-1 rounded-full text-sm font-bold ${taxa >= 70 ? "bg-green-100 text-green-700" : taxa >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{taxa}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="flex gap-3">
                <button disabled className="flex-1 bg-blue-600 text-white text-sm font-semibold py-3 rounded-xl min-h-11 opacity-40 cursor-not-allowed" title="Em breve">📄 Baixar PDF</button>
                <button disabled className="flex-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-3 flex items-center justify-center opacity-40 cursor-not-allowed min-h-11" title="Em breve"><p className="text-xs text-gray-400 text-center">📧 WhatsApp</p></button>
              </div>
            </>
          )}
        </>
      )}

      {secao === "cultura" && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 border border-gray-100 dark:border-gray-800">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">🌾 Selecionar cultura e período</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cultura *</label>
                <select value={culturaSelecionada} onChange={e => setCulturaSelecionada(e.target.value)} className={inputClass + " w-full mt-1.5"}>
                  <option value="">Selecione uma cultura...</option>
                  {CULTURAS_TODAS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex gap-2 flex-wrap">
                <input type="date" value={dataInicioCultura} onChange={e => setDataInicioCultura(e.target.value)} className={inputClass + " flex-1 min-w-36"} />
                <input type="date" value={dataFimCultura} onChange={e => setDataFimCultura(e.target.value)} className={inputClass + " flex-1 min-w-36"} />
              </div>
              {/* Filtro de status */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Filtrar por tipo</label>
                <select value={filtroStatusCultura} onChange={e => { setFiltroStatusCultura(e.target.value); setRelatorioIA(""); }} className={inputClass + " w-full mt-1.5"}>
                  <option value="todos">📋 Todas as atividades</option>
                  <option value="concluida">✅ Apenas concluídas</option>
                  <option value="atrasada">🔴 Apenas atrasadas</option>
                  <option value="em_execucao">▶️ Em execução</option>
                  <option value="pausada">⏸️ Pausadas</option>
                  <option value="planejada">📋 Planejadas</option>
                  <option value="nao_planejada">🆕 Não planejadas</option>
                </select>
              </div>
              <button onClick={buscarHistoricoCultura} disabled={carregandoCultura || !culturaSelecionada}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold py-3 rounded-xl text-sm transition min-h-11">
                {carregandoCultura ? "Buscando..." : "🔍 Buscar histórico"}
              </button>
            </div>
          </div>

          {relatorioCultura && (
            <>
              <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 border border-gray-100 dark:border-gray-800">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">🌾 {relatorioCultura.cultura}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{relatorioCultura.fazenda} · {tarefasExibidas.length} de {relatorioCultura.total} atividades</p>
                  </div>
                  <span className="text-xs bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-3 py-1 rounded-full font-semibold">{tarefasExibidas.length} registros</span>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {tarefasExibidas.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">Nenhuma atividade encontrada para este filtro.</p>
                  ) : tarefasExibidas.map((t, i) => {
                    const s = statusInfo[t.status];
                    return (
                      <div key={i} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                        <p className="text-xs font-semibold text-gray-800 dark:text-white truncate">{t.titulo}</p>
                        <div className="flex gap-1.5 mt-1 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s?.bg} ${s?.text}`}>{s?.emoji} {s?.label}</span>
                          {t.area && <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full">📍 {t.area}</span>}
                          {t.concluido_em && <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">✅ {new Date(t.concluido_em).toLocaleDateString("pt-BR")}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {!relatorioIA && (
                <button onClick={gerarRelatorioIA} disabled={gerandoIA || tarefasExibidas.length === 0}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold py-3 rounded-xl text-sm transition min-h-11 flex items-center justify-center gap-2">
                  {gerandoIA ? <><div className="w-2 h-2 bg-white rounded-full animate-pulse" />Gerando com IA...</> : "✨ Gerar relatório técnico com IA"}
                </button>
              )}

              {gerandoIA && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-xl">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <p className="text-sm text-green-700 dark:text-green-400 font-medium">Analisando histórico de atividades...</p>
                  </div>
                  <div className="mt-2 h-1 bg-green-100 dark:bg-green-800 rounded-full overflow-hidden"><div className="h-full bg-green-500 rounded-full animate-pulse w-3/4" /></div>
                </div>
              )}

              {relatorioIA && (
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 border border-gray-100 dark:border-gray-800">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-bold text-gray-900 dark:text-white">✨ Relatório Técnico — IA</p>
                    <button onClick={gerarRelatorioIA} disabled={gerandoIA} className="text-xs text-green-600 dark:text-green-400 font-semibold px-3 py-1.5 rounded-lg border border-green-200 dark:border-green-800 hover:bg-green-50 transition min-h-11">Regenerar</button>
                  </div>
                  <div className="space-y-1 text-xs text-gray-700 dark:text-gray-300 leading-relaxed max-h-96 overflow-y-auto">
                    {linhasRelatorio.map((linha, i) => {
                      const isRepeticao = linha.includes("⚠️") || linha.toLowerCase().includes("repetição");
                      return <p key={i} className={isRepeticao ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 font-semibold px-2 py-1 rounded-lg border border-red-200 dark:border-red-800" : ""}>{linha || "\u00A0"}</p>;
                    })}
                  </div>
                  <div className="mt-4 space-y-3">
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                      <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">📱 Enviar por WhatsApp</p>
                      <div className="flex gap-2">
                        <select value={destinatario} onChange={e => setDestinatario(e.target.value)} className="flex-1 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-700 dark:text-white text-xs focus:outline-none focus:ring-2 focus:ring-green-500 min-h-11">
                          <option value="">Selecionar destinatário...</option>
                          {usuario?.whatsapp && <option value={usuario.whatsapp}>Para mim ({usuario.nome.split(" ")[0]})</option>}
                          {usuarios.filter(u => u.id !== usuario?.id).map(u => <option key={u.id} value={u.whatsapp}>{u.nome} ({perfilLabel[u.perfil]})</option>)}
                        </select>
                        <button onClick={enviarWhatsapp} disabled={enviandoWhatsapp || !destinatario} className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold px-4 py-2 rounded-xl text-xs transition min-h-11">{enviandoWhatsapp ? "..." : "📱 Enviar"}</button>
                      </div>
                    </div>
                    <button disabled className="w-full flex items-center justify-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800 opacity-40 cursor-not-allowed min-h-11" title="Em breve"><p className="text-xs text-blue-600 dark:text-blue-400 font-medium">📄 Exportar PDF</p></button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
